import {
  BudgetInputPrice,
  EditedValues,
  MassUpdateInputPricePayloadItem,
} from './types';
import {
  isPositiveCurrency,
  normalizeCurrencyValue,
  roundToCurrency,
} from './validationUtils';

const hasOwnProperty = (obj: EditedValues, key: number): boolean => {
  return Object.prototype.hasOwnProperty.call(obj, key);
};

type EditedValueChange = {
  id: number;
  value: number | null;
};

const getEditedValueChange = (
  item: BudgetInputPrice,
  value: number | null | undefined,
): EditedValueChange => {
  const nextValue = normalizeCurrencyValue(value);
  const originalValue = getOriginalUnitPrice(item);

  if (nextValue === originalValue) {
    return {
      id: item.id,
      value: null,
    };
  }

  return {
    id: item.id,
    value: nextValue,
  };
};

export const getOriginalUnitPrice = (item: BudgetInputPrice): number => {
  const source =
    typeof item.total === 'number' ? item.total : item.total_original || 0;

  return roundToCurrency(source);
};

export const getCurrentUnitPrice = (
  item: BudgetInputPrice,
  editedValues: EditedValues,
): number => {
  if (hasOwnProperty(editedValues, item.id)) {
    return editedValues[item.id];
  }

  return getOriginalUnitPrice(item);
};

export const setEditedValue = (
  editedValues: EditedValues,
  item: BudgetInputPrice,
  value: number | null | undefined,
): EditedValues => {
  const change = getEditedValueChange(item, value);

  if (change.value === null) {
    if (!hasOwnProperty(editedValues, change.id)) {
      return editedValues;
    }

    const { [change.id]: _, ...rest } = editedValues;
    return rest;
  }

  if (editedValues[change.id] === change.value) {
    return editedValues;
  }

  return {
    ...editedValues,
    [change.id]: change.value,
  };
};

export const clearEditedValuesForIds = (
  editedValues: EditedValues,
  ids: number[],
): EditedValues => {
  if (ids.length === 0) {
    return editedValues;
  }

  const idsToClear = new Set(ids);
  let hasChanges = false;

  idsToClear.forEach((id) => {
    if (hasOwnProperty(editedValues, id)) {
      hasChanges = true;
    }
  });

  if (!hasChanges) {
    return editedValues;
  }

  const nextEditedValues = { ...editedValues };

  idsToClear.forEach((id) => {
    delete nextEditedValues[id];
  });

  return nextEditedValues;
};

export const applyFixedPriceToIds = (
  editedValues: EditedValues,
  items: BudgetInputPrice[],
  ids: number[],
  fixedPrice: number | null | undefined,
): EditedValues => {
  if (ids.length === 0) {
    return editedValues;
  }

  const itemById = new Map(items.map((item) => [item.id, item]));
  const nextEditedValues = { ...editedValues };
  let hasChanges = false;

  ids.forEach((id) => {
    const item = itemById.get(id);

    if (item) {
      const change = getEditedValueChange(item, fixedPrice);

      if (change.value === null) {
        if (hasOwnProperty(nextEditedValues, change.id)) {
          delete nextEditedValues[change.id];
          hasChanges = true;
        }

        return;
      }

      if (nextEditedValues[change.id] !== change.value) {
        nextEditedValues[change.id] = change.value;
        hasChanges = true;
      }
    }
  });

  return hasChanges ? nextEditedValues : editedValues;
};

export const applyPercentageToIds = (
  editedValues: EditedValues,
  items: BudgetInputPrice[],
  ids: number[],
  percentage: number | null | undefined,
): EditedValues => {
  if (ids.length === 0) {
    return editedValues;
  }

  const normalizedPercentage =
    typeof percentage === 'number' && Number.isFinite(percentage)
      ? percentage
      : null;

  if (normalizedPercentage === null) {
    return editedValues;
  }

  const itemById = new Map(items.map((item) => [item.id, item]));
  const nextEditedValues = { ...editedValues };
  let hasChanges = false;

  ids.forEach((id) => {
    const item = itemById.get(id);

    if (!item) {
      return;
    }

    const currentValue = getCurrentUnitPrice(item, nextEditedValues);
    const updatedValue = currentValue * (1 + normalizedPercentage / 100);
    const change = getEditedValueChange(item, updatedValue);

    if (change.value === null) {
      if (hasOwnProperty(nextEditedValues, change.id)) {
        delete nextEditedValues[change.id];
        hasChanges = true;
      }

      return;
    }

    if (nextEditedValues[change.id] !== change.value) {
      nextEditedValues[change.id] = change.value;
      hasChanges = true;
    }
  });

  return hasChanges ? nextEditedValues : editedValues;
};

export const getModifiedIds = (editedValues: EditedValues): number[] => {
  return Object.keys(editedValues)
    .map((id) => Number(id))
    .sort((a, b) => a - b);
};

export const getInvalidEditedIds = (editedValues: EditedValues): number[] => {
  return getModifiedIds(editedValues).filter((id) => {
    return !isPositiveCurrency(editedValues[id]);
  });
};

export const buildMassUpdatePayload = (
  editedValues: EditedValues,
): MassUpdateInputPricePayloadItem[] => {
  return getModifiedIds(editedValues)
    .filter((id) => isPositiveCurrency(editedValues[id]))
    .map((id) => ({
      input_price_id: id,
      unit_price: roundToCurrency(editedValues[id]),
    }));
};
